import type {
  V2ElectricMapDataAreaV2PostResponse,
  V2LightmapAreaPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import type { UnityClickDetail, RenderStreamingVideoElement } from '../../../UnityRenderMap';
import { getUnityPosition, notEmpty } from '../../../UnityRenderMap/utils';

export type AreaItem = Exclude<V2LightmapAreaPostResponse['list'], undefined>[number];
export type StationItem = Exclude<V2ElectricMapDataAreaV2PostResponse['list'], undefined>[number];

export const EMPTY_AREA_LIST: AreaItem[] = [];
export const LIGHT_PARENT = 'light';
export const LIGHT_LABEL_PARENT = 'LightLabel';
export const WORKSHOP_PARENT = 'Energy';
export const WORKSHOP_LABEL_PARENT = 'CustomLabel';
export const WORKSHOP_LABEL_ALWAYS_VISIBLE_THRESHOLD = 3;
export const WORKSHOP_LABEL_HEIGHT = 90;

export const getLightInstanceName = (area: AreaItem) => String(area.id ?? area.zoneName ?? 'unknown');

export const getSunLightInstanceName = (area: AreaItem) => `sun-${area.id ?? area.zoneName ?? 'unknown'}`;

export const getWorkshopInstanceName = (area: AreaItem) => area.zoneName ?? `workshop-${area.id ?? 'unknown'}`;

export const hasMatchingAreaIdentity = (area: AreaItem, candidates: string[], instanceName: string) =>
  [instanceName, String(area.id ?? ''), area.zoneName, area.zoneCode]
    .filter(Boolean)
    .some(value => candidates.includes(String(value).trim()));

export const createWorkshopLabelText = (count: number, openCount: number, closeCount: number, offlineCount: number) =>
  `<size=12><color=#FFFFFFD9><pos=0>车间照明<pos=52>开启<pos=82>关闭<pos=112>离线</color></size>\n<size=16><b><color=#FFFFFF><pos=14>${count}</color><color=#59D744><pos=58>${openCount}</color><color=#FFFFFF><pos=88>${closeCount}</color><color=#FF9247><pos=118>${offlineCount}</color></b></size>`;

export const getStateLabel = (state?: number) => {
  if (state === 1) return '开';
  if (state === 0) return '关';
  if (state === 2) return '离线';
  return undefined;
};

type LightMarkerType = '高杆灯' | '太阳能灯';

export const LIGHT_STATE_TYPE_MAP: Record<LightMarkerType, number> = {
  高杆灯: 1,
  太阳能灯: 3,
};

export const getLightStateData = (area: AreaItem, lightType: LightMarkerType) =>
  area.stateData?.find(item => item.type === LIGHT_STATE_TYPE_MAP[lightType]);

export const getLightDetail = (area: AreaItem, lightType: LightMarkerType, instanceName: string) => {
  const stateData = getLightStateData(area, lightType);

  return {
    id: area.id,
    name: instanceName,
    zoneName: area.zoneName,
    zoneCode: area.zoneCode,
    ggdDeviceIds: area.ggdDeviceIds,
    lightDeviceIds: area.lightDeviceIds,
    lightCurrentStatus: getStateLabel(stateData?.state),
    lightType,
    labelType: lightType === '太阳能灯' ? 'sunLight' : 'ggdLight',
    lightMsgs: [],
    deviceId: stateData?.deviceId,
    deviceName: stateData?.deviceName,
    stateData,
    properties: area,
  };
};

export const createGgdLightPayload = (
  ggdList: AreaItem[],
  videoElement?: RenderStreamingVideoElement,
  visible = true
) => {
  const instances = visible
    ? ggdList
        .map(area => {
          const position = getUnityPosition(area, videoElement, '[UnityLightMap]');
          if (!position) {
            return null;
          }
          const name = getLightInstanceName(area);

          return {
            name,
            position,
            rotation: [0, 0, 0] as [number, number, number],
            grounded: true,
            parent: LIGHT_PARENT,
            properties: getLightDetail(area, '高杆灯', name),
          };
        })
        .filter(notEmpty)
    : [];

  return {
    type: 'refreshModel' as const,
    data: {
      remove: !visible
        ? [
            {
              path: 'Prefabs/light',
              type: 'prefab' as const,
              instances: ggdList.map(area => ({
                name: getLightInstanceName(area),
                parent: LIGHT_PARENT,
              })),
            },
            {
              path: 'Prefabs/LightLabelRoot',
              type: 'prefab' as const,
              instances: ggdList.map(area => ({
                name: getLightInstanceName(area),
                parent: LIGHT_LABEL_PARENT,
              })),
            },
          ]
        : [],
      add:
        visible && instances.length
          ? [
              {
                path: 'Prefabs/light',
                type: 'prefab' as const,
                instances,
                label: {
                  path: 'Prefabs/LightLabelRoot',
                  height: 70,
                  parent: LIGHT_LABEL_PARENT,
                },
              },
            ]
          : [],
    },
  };
};

export const createSunLightPayload = (
  sunList: AreaItem[],
  videoElement?: RenderStreamingVideoElement,
  visible = true
) => {
  const instances = visible
    ? sunList
        .map(area => {
          const position = getUnityPosition(area, videoElement, '[UnityLightMap]');
          if (!position) {
            return null;
          }
          const name = getSunLightInstanceName(area);

          return {
            name,
            position,
            rotation: [0, 0, 0] as [number, number, number],
            grounded: true,
            parent: LIGHT_PARENT,
            properties: getLightDetail(area, '太阳能灯', name),
          };
        })
        .filter(notEmpty)
    : [];

  return {
    type: 'refreshModel' as const,
    data: {
      remove: !visible
        ? [
            {
              path: 'Prefabs/light',
              type: 'prefab' as const,
              instances: sunList.map(area => ({
                name: getSunLightInstanceName(area),
                parent: LIGHT_PARENT,
              })),
            },
            {
              path: 'Prefabs/LightLabelRoot',
              type: 'prefab' as const,
              instances: sunList.map(area => ({
                name: getSunLightInstanceName(area),
                parent: LIGHT_LABEL_PARENT,
              })),
            },
          ]
        : [],
      add:
        visible && instances.length
          ? [
              {
                path: 'Prefabs/light',
                type: 'prefab' as const,
                instances,
                label: {
                  path: 'Prefabs/LightLabelRoot',
                  height: 70,
                  parent: LIGHT_LABEL_PARENT,
                },
              },
            ]
          : [],
    },
  };
};

export const createWorkshopLightPayload = (
  workshopList: AreaItem[],
  videoElement?: RenderStreamingVideoElement,
  visible = true
) => {
  const instances = visible
    ? workshopList
        .map(area => {
          const position = getUnityPosition(area, videoElement, '[UnityLightMap]');
          if (!position) {
            return null;
          }

          const workshopData = area.circuitData ?? area.lightData;
          const count = workshopData?.count ?? 0;
          const openCount = workshopData?.openCount ?? 0;
          const closeCount = workshopData?.closeCount ?? 0;
          const offlineCount = workshopData?.offlineCount ?? 0;

          return {
            name: getWorkshopInstanceName(area),
            position,
            rotation: [0, 0, 0] as [number, number, number],
            grounded: false,
            parent: WORKSHOP_PARENT,
            properties: {
              id: area.id,
              name: getWorkshopInstanceName(area),
              areaName: area.zoneName,
              zoneCode: area.zoneCode,
              zoneName: area.zoneName,
              pointLng: area.pointLng,
              pointLat: area.pointLat,
              circuitDeviceIds: area.circuitDeviceIds,
              circuitData: workshopData,
              labelType: 'workshopLight',
              key1: createWorkshopLabelText(count, openCount, closeCount, offlineCount),
              count,
              openCount,
              closeCount,
              offlineCount,
            },
          };
        })
        .filter(notEmpty)
    : [];
  const alwaysVisibleLabelInstances = instances.filter(
    instance => instance.properties.count > WORKSHOP_LABEL_ALWAYS_VISIBLE_THRESHOLD
  );
  const hiddenByDefaultInstances = instances.filter(
    instance => instance.properties.count <= WORKSHOP_LABEL_ALWAYS_VISIBLE_THRESHOLD
  );
  const addItems = [];

  if (visible && alwaysVisibleLabelInstances.length) {
    addItems.push({
      path: 'Prefabs/Empty',
      type: 'prefab' as const,
      instances: alwaysVisibleLabelInstances,
      label: {
        path: 'Prefabs/purpleLabelRoot',
        height: WORKSHOP_LABEL_HEIGHT,
        parent: WORKSHOP_LABEL_PARENT,
      },
    });
  }

  if (visible && hiddenByDefaultInstances.length) {
    addItems.push({
      path: 'Prefabs/Empty',
      type: 'prefab' as const,
      instances: hiddenByDefaultInstances,
    });
  }

  return {
    type: 'refreshModel' as const,
    data: {
      remove: [
        {
          path: 'Prefabs/Empty',
          type: 'prefab' as const,
          instances: workshopList.map(area => ({
            name: getWorkshopInstanceName(area),
            parent: WORKSHOP_PARENT,
          })),
        },
        {
          path: 'Prefabs/purpleLabelRoot',
          type: 'prefab' as const,
          instances: workshopList.map(area => ({
            name: getWorkshopInstanceName(area),
            parent: WORKSHOP_LABEL_PARENT,
          })),
        },
      ],
      add: addItems,
    },
  };
};
